/**
 * The bell's data: the caller's latest notifications and unread count, mark read.
 *
 * RLS returns only the caller's own rows, and the only column they may change is
 * `read_at` (a column grant), so "mark read" is a plain update of that column.
 *
 * Fresh: Supabase Realtime pushes new rows when it is available (the real project); the
 * local stack has no Realtime, so the list also polls every 30 s, and refetches whenever
 * the panel opens or the window regains focus.
 */
import type { Tables } from '@gymcoach/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useMe } from '@/auth/useMe';
import { supabase } from '@/lib/supabase';

import { realtimeAvailable } from './realtime';

export type BellNotification = Pick<Tables<'notifications'>, 'id' | 'type' | 'title' | 'body' | 'data' | 'read_at' | 'created_at'>;

export interface BellData {
  items: BellNotification[];
  unread: number;
}

export const BELL_LIMIT = 20;
const POLL_MS = 30_000;
const POLL_WHEN_LIVE_MS = 5 * 60_000;

export const bellKey = (userId: string) => ['notifications', 'bell', userId] as const;

const REALTIME = realtimeAvailable(
  import.meta.env.VITE_SUPABASE_URL as string | undefined,
  import.meta.env.VITE_REALTIME as string | undefined,
);

/** Subscribes to the caller's new notifications; true once Realtime confirms. */
function useLive(userId: string): boolean {
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!REALTIME) return;
    let cancelled = false;
    const channel = supabase
      .channel(`bell:${userId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, () => {
        void queryClient.invalidateQueries({ queryKey: bellKey(userId) });
      })
      .subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') setLive(true);
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // No Realtime here (the local stack): stop retrying; polling carries on.
          setLive(false);
          void supabase.removeChannel(channel);
        }
      });
    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
  return live;
}

export function useBell() {
  const { userId } = useMe();
  const live = useLive(userId);
  return useQuery({
    queryKey: bellKey(userId),
    refetchInterval: live ? POLL_WHEN_LIVE_MS : POLL_MS,
    staleTime: 10_000,
    queryFn: async (): Promise<BellData> => {
      const [list, count] = await Promise.all([
        supabase
          .from('notifications')
          .select('id, type, title, body, data, read_at, created_at')
          .order('created_at', { ascending: false })
          .limit(BELL_LIMIT),
        supabase.from('notifications').select('id', { count: 'exact', head: true }).is('read_at', null),
      ]);
      if (list.error) throw list.error;
      if (count.error) throw count.error;
      return { items: list.data, unread: count.count ?? 0 };
    },
  });
}

/** Marks one notification read (optimistically: the dot disappears at once). */
export function useMarkRead() {
  const { userId } = useMe();
  const queryClient = useQueryClient();
  const key = bellKey(userId);
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .is('read_at', null);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BellData>(key);
      if (previous) {
        const target = previous.items.find((n) => n.id === id);
        if (target && !target.read_at) {
          queryClient.setQueryData<BellData>(key, {
            items: previous.items.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
            unread: Math.max(0, previous.unread - 1),
          });
        }
      }
      return { previous };
    },
    onError: (_e, _id, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** Marks every unread notification read, including those beyond the latest 20. */
export function useMarkAllRead() {
  const { userId } = useMe();
  const queryClient = useQueryClient();
  const key = bellKey(userId);
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('read_at', null);
      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BellData>(key);
      if (previous) {
        const now = new Date().toISOString();
        queryClient.setQueryData<BellData>(key, {
          items: previous.items.map((n) => (n.read_at ? n : { ...n, read_at: now })),
          unread: 0,
        });
      }
      return { previous };
    },
    onError: (_e, _v, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}
